import { normalizeLogAttributes } from './normalizeAttributes';
import {
  CaptureErrorOptions,
  EventMap,
  Logger,
  LoggerUserContext,
  LogParams,
  TrackOptions,
} from './types';

export class ZEPLogger {
  public name = 'ZEPLogger';
  private _initialState: Record<string, unknown>;
  private _state: Record<string, unknown>;
  private _loggers: Logger[];
  private _persistentContext: Record<string, unknown>;
  private _profileProperties: Record<string, unknown>;
  private _userContext: Record<string, unknown>;
  private _currentUserContext: LoggerUserContext | null;
  private _identifiedUserId: string | null;
  private _tags: Record<string, string | number | boolean>;

  constructor({
    initialState = {},
    loggers = [],
  }: {
    initialState?: object;
    loggers?: Logger[];
  }) {
    this._initialState = initialState as Record<string, unknown>;
    this._loggers = loggers;
    this._persistentContext = {};
    this._profileProperties = {};
    this._userContext = {};
    this._currentUserContext = null;
    this._identifiedUserId = null;
    this._tags = {};
    this._state = this._composeState();
    this._bindPublicMethods();
  }

  /**
   * @zep/logger의 기존 Proxy facade는 함수 property 접근 시 target에 bind된 함수를 반환했습니다.
   * stable ZEPLogger facade를 직접 export해도 `const { track } = logger` 같은 기존 호출 방식이
   * 깨지지 않도록 public 메서드는 인스턴스에 고정합니다.
   */
  private _bindPublicMethods(): void {
    this.configure = this.configure.bind(this);
    this.getConfigurationSnapshot = this.getConfigurationSnapshot.bind(this);
    this.setState = this.setState.bind(this);
    this.setUserProperties = this.setUserProperties.bind(this);
    this.setUserContext = this.setUserContext.bind(this);
    this.clearUserContext = this.clearUserContext.bind(this);
    this.resetState = this.resetState.bind(this);
    this.track = this.track.bind(this);
    this.debug = this.debug.bind(this);
    this.captureError = this.captureError.bind(this);
    this.setTag = this.setTag.bind(this);
    this.identify = this.identify.bind(this);
  }

  /**
   * leaf logger와 initialState를 재설정하되, 런타임에 쌓인 context는 유지합니다.
   *
   * registerLogger()가 앱별 logger 인스턴스를 다시 등록하더라도 AuthContext/space/map
   * 등에서 이미 주입한 상태가 사라지지 않도록 하기 위한 state-owning facade API입니다.
   */
  configure({
    initialState = {},
    loggers = [],
  }: {
    initialState?: object;
    loggers?: Logger[];
  }) {
    this._initialState = initialState as Record<string, unknown>;
    this._loggers = [...loggers];
    this._syncState();
    this._syncProfileProperties();
    this._syncUserContext();
    this._syncIdentify();
    this._syncTags();
  }

  /**
   * registerLogger() 호환을 위한 구성 스냅샷입니다.
   * leaf logger 인스턴스는 SDK client 상태를 가진 sink singleton이므로 참조를 유지합니다.
   */
  getConfigurationSnapshot(): {
    initialState: Record<string, unknown>;
    loggers: Logger[];
  } {
    return {
      initialState: { ...this._initialState },
      loggers: [...this._loggers],
    };
  }

  setState(state: object) {
    this._persistentContext = {
      ...this._persistentContext,
      ...(state as Record<string, unknown>),
    };
    this._syncState();
  }

  setUserProperties(userProperties: object) {
    this._profileProperties = {
      ...this._profileProperties,
      ...(userProperties as Record<string, unknown>),
    };
    this._syncState();
    this._syncProfileProperties();
  }

  /**
   * 로그인한 사용자 컨텍스트를 이벤트 상태와 leaf logger user API에 반영합니다.
   * 도메인 User 모델은 앱에서 평탄화하고, 통합 로거는 식별자와 속성만 받습니다.
   */
  setUserContext(context: LoggerUserContext) {
    const { userId, properties, integrations } = context;
    this._currentUserContext = {
      userId,
      properties: { ...properties },
      integrations,
    };
    this._userContext = { ...properties };
    this._syncState();
    this._syncUserContext();
  }

  /**
   * leaf의 setUserContext에는 profile/user properties를 함께 전달합니다.
   * 기존 setUserProperties + setUserContext 조합에서 profile 속성이 누락되지 않게 하기 위함입니다.
   */
  private _syncUserContext(): void {
    if (!this._currentUserContext) return;

    const { userId, integrations } = this._currentUserContext;
    for (const logger of this._loggers) {
      if (logger.setUserContext) {
        logger.setUserContext({
          userId,
          properties: this._composeProfileProperties(),
          integrations,
        });
        continue;
      }

      logger.identify?.(userId);
    }
  }

  /**
   * 로그인 사용자 컨텍스트만 제거하고, initialState와 세션/디바이스성 userProperties는 유지합니다.
   */
  clearUserContext(state: object = {}) {
    this._currentUserContext = null;
    this._identifiedUserId = null;
    this._userContext = {};
    this._persistentContext = {
      ...this._persistentContext,
      ...(state as Record<string, unknown>),
    };
    this._syncState();

    for (const logger of this._loggers) {
      logger.clearUserContext?.(state);
    }
  }

  resetState() {
    this._persistentContext = {};
    this._syncState();
  }

  private _composeState(): Record<string, unknown> {
    return {
      ...this._initialState,
      ...this._persistentContext,
      ...this._profileProperties,
      ...this._userContext,
    };
  }

  private _composeProfileProperties(): Record<string, unknown> {
    return {
      ...this._profileProperties,
      ...this._userContext,
    };
  }

  private _syncState(): void {
    this._state = this._composeState();
    for (const logger of this._loggers) {
      logger.setState?.(this._state);
    }
  }

  private _syncProfileProperties(): void {
    for (const logger of this._loggers) {
      logger.setUserProperties?.(this._composeProfileProperties());
    }
  }

  private _composeEventParams(
    logger: Logger,
    params?: Record<string, unknown>,
  ): Record<string, unknown> {
    const eventParams = params ?? {};

    if (logger.mapEventParams) {
      return normalizeLogAttributes(
        logger.mapEventParams({
          globalState: this._state,
          eventParams,
        }),
      );
    }

    return normalizeLogAttributes({
      ...this._state,
      ...eventParams,
    });
  }

  public track<K extends keyof EventMap>(
    message: K,
    params: EventMap[K],
    options?: TrackOptions,
  ): void;
  public track(
    message: string,
    params?: Record<string, unknown>,
    options?: TrackOptions,
  ): void;
  public track(
    message: string,
    params?: Record<string, unknown>,
    options?: TrackOptions,
  ): void {
    for (const logger of this._loggers) {
      const role = logger.role ?? 'all';
      if (role === 'tracking' || role === 'all') {
        try {
          const eventParams = this._composeEventParams(logger, params);
          if (!logger.track) {
            logger.log(message as string, eventParams);
          } else if (options) {
            logger.track(message as string, eventParams, options);
          } else {
            logger.track(message as string, eventParams);
          }
        } catch (error) {
          console.error('Failed to track', error);
        }
      }
    }
  }

  public debug(message: string, params?: LogParams): void {
    for (const logger of this._loggers) {
      const role = logger.role ?? 'all';
      if (role === 'debug' || role === 'all') {
        try {
          if (logger.debug) {
            logger.debug(message, this._composeEventParams(logger, params));
          }
        } catch (error) {
          console.error('Failed to debug log', error);
        }
      }
    }
  }

  public captureError(error: unknown, options?: CaptureErrorOptions): void {
    for (const logger of this._loggers) {
      const role = logger.role ?? 'all';
      if ((role === 'error' || role === 'all') && logger.captureError) {
        try {
          logger.captureError(error, options);
        } catch (e) {
          console.error('Failed to captureError', e);
        }
      }
    }
  }

  public setTag(key: string, value: string | number | boolean): void {
    this._tags[key] = value;
    for (const logger of this._loggers) {
      if (logger.setTag) {
        try {
          logger.setTag(key, value);
        } catch (e) {
          console.error('Failed to setTag', e);
        }
      }
    }
  }

  private _syncTags(): void {
    for (const [key, value] of Object.entries(this._tags)) {
      for (const logger of this._loggers) {
        logger.setTag?.(key, value);
      }
    }
  }

  public identify(userId: string): void {
    this._identifiedUserId = userId;
    for (const logger of this._loggers) {
      if (logger.identify) {
        try {
          logger.identify(userId);
        } catch (error) {
          console.error('Failed to identify', error);
        }
      }
    }
  }

  private _syncIdentify(): void {
    if (!this._identifiedUserId) return;
    for (const logger of this._loggers) {
      logger.identify?.(this._identifiedUserId);
    }
  }
}
